import type { TFunction } from 'i18next'

/**
 * Task `type` values are stored as-is in the API. The admin create form historically saved
 * English display strings ("Inspect", "Feed Sugar Syrup", ...) while recommendations use
 * camelCase keys ("varroaTreatment"). Both map to a `tasks.types.<key>` translation here;
 * anything else (free-text custom types) is shown unchanged.
 */
const LEGACY_TASK_TYPE_KEYS: Record<string, string> = {
  Inspect: 'inspection',
  Treatment: 'treatment',
  Move: 'movement',
  Harvest: 'harvest',
  'Feed Sugar Syrup': 'sugarSyrup',
  'Feed Pollen Substitute': 'pollenSubstitute',
  'Requeen Hive': 'requeenHive',
  'Replace Old Frames': 'combReplacement',
  'Clean Hive Equipment': 'cleanEquipment',
  'Weigh Hives': 'weighing',
  Custom: 'custom',
}

const TASK_TYPE_KEYS = new Set([
  'inspection', 'treatment', 'varroaTreatment', 'harvest', 'movement', 'feeding', 'sugarSyrup',
  'pollenSubstitute', 'requeenHive', 'splitting', 'combReplacement', 'cleanEquipment', 'winterPrep',
  'springPrep', 'queenCheck', 'pesticideAlert', 'swarmPrevention', 'superAdd', 'superRemove',
  'waxMothTreatment', 'hiveInsulation', 'pollinationService', 'broodCheck', 'droneRemoval',
  'apiarySitePrep', 'honeyExtraction', 'beePackageInstall', 'medicationCheck', 'waterSource',
  'queenRearing', 'robberBeePrevention', 'weighing', 'custom',
])

/**
 * Returns the display label for a stored task type in the current UI language.
 * @param t - i18next translate function
 * @param type - stored task type value
 * @returns translated label, or the raw value for custom free-text types
 */
export function getTaskTypeLabel(t: TFunction, type?: string | null): string {
  if (!type) return '-'
  const key = LEGACY_TASK_TYPE_KEYS[type] || (TASK_TYPE_KEYS.has(type) ? type : null)
  return key ? t(`tasks.types.${key}`, { defaultValue: type }) : type
}

/**
 * Formats a date in the current UI language (month names follow the language).
 * @param date - date value
 * @param lang - i18n language code
 * @param options - Intl date format options
 * @returns localized date string
 */
export function formatTaskDate(date: string | number | Date, lang: string | undefined, options: Intl.DateTimeFormatOptions): string {
  const d = new Date(date)
  if (Number.isNaN(d.getTime())) return ''
  try {
    return d.toLocaleString(lang || 'en', options)
  } catch {
    return d.toLocaleString('en', options)
  }
}
